import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Phone, MapPin, ShoppingBag, Star, Download, Eye, Loader2, X } from 'lucide-react';
import { fetchCustomers } from '../api';
import Pagination from './Pagination';

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [showBillModal, setShowBillModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const itemsPerPage = 6;
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setIsLoading(true);
        const data = await fetchCustomers();
        setCustomers(data);
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomers();
  }, []);

  const sortedCustomers = customers.sort((a, b) => a.name.localeCompare(b.name));

  const filteredCustomers = sortedCustomers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    if (customerFilter === 'vip') {
      matchesFilter = c.status === 'VIP' || c.status === 'Premium';
    } else if (customerFilter === 'highOrders') {
      const avgOrders = customers.length > 0 ? customers.reduce((sum, customer) => sum + customer.totalOrders, 0) / customers.length : 0;
      matchesFilter = c.totalOrders >= avgOrders;
    }
    
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter) => {
    setCustomerFilter(filter);
    setCurrentPage(1);
  };

  const handleViewBill = (order, orderIndex) => {
    setSelectedOrder({ ...order, orderNumber: orderIndex + 1, customer: selectedCustomer });
    setShowBillModal(true);
  };

  const handleViewOrders = (customer) => {
    setSelectedCustomer(customer);
    setShowOrderModal(true);
  };

  const getStatusColorClass = (status) => {
    switch(status) {
      case 'VIP': return 'badge-info';
      case 'Premium': return 'badge-warning';
      case 'Regular': return 'badge-primary-alt';
      case 'New': return 'badge-success';
      default: return 'bg-slate-100 text-slate-700 border border-slate-200/60 px-3 py-1 rounded-full text-xs font-bold shadow-sm inline-flex items-center';
    }
  };

  const totalCustomers = customers.length;
  const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
  const totalOrdersCount = customers.reduce((sum, c) => sum + c.totalOrders, 0);
  const avgOrderValue = totalOrdersCount > 0 ? totalRevenue / totalOrdersCount : 0;
  const vipCustomers = customers.filter(c => c.status === 'VIP' || c.status === 'Premium').length;

  const exportToPDF = () => {
    const printContent = `
      <html>
        <head>
          <title>Customer Data Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #1e293b; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; }
            h1 { color: #059669; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-size: 13px; }
            th { background-color: #f8fafc; font-weight: bold; color: #475569; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🌿 Manthuli Organic Shop</h1>
            <h2>Customer Insights & Relationships Report</h2>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Total Orders</th>
                <th>Total Spent</th>
                <th>Last Order</th>
                <th>Status</th>
                <th>Loyalty Points</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCustomers.map(customer => `
                <tr>
                  <td><b>${customer.name}</b></td>
                  <td>${customer.email}</td>
                  <td>${customer.phone}</td>
                  <td>${customer.location}</td>
                  <td>${customer.totalOrders}</td>
                  <td>₹${customer.totalSpent.toLocaleString()}</td>
                  <td>${customer.lastOrder}</td>
                  <td>${customer.status}</td>
                  <td>${customer.loyaltyPoints}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight">
              Customer Management
            </h1>
            <p className="text-slate-500 mt-1.5 font-medium">Analyze details and loyalty metrics of your registered shoppers.</p>
          </div>
          
          <button 
            onClick={exportToPDF}
            className="ui-btn-secondary"
          >
            <Download size={15} />
            <span>Export to PDF</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="group cursor-pointer" onClick={() => handleFilterChange('all')}>
          <div className={`ui-card ui-card-pad shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-br from-white to-blue-50/20 border-slate-100 ${
            customerFilter === 'all' ? 'ring-2 ring-emerald-500/20 border-emerald-500/40' : ''
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/10">
                <Users size={20} />
              </div>
            </div>
            <h3 className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">Total Customers</h3>
            <p className="text-2xl font-extrabold text-slate-800 tracking-tight">{totalCustomers}</p>
          </div>
        </div>
        <div className="group cursor-pointer" onClick={() => handleFilterChange('highOrders')}>
          <div className={`ui-card ui-card-pad shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-br from-white to-orange-50/20 border-slate-100 ${
            customerFilter === 'highOrders' ? 'ring-2 ring-emerald-500/20 border-emerald-500/40' : ''
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md shadow-orange-500/10">
                <ShoppingBag size={20} />
              </div>
            </div>
            <h3 className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">Average Orders</h3>
            <p className="text-2xl font-extrabold text-slate-800 tracking-tight">
              {customers.length > 0 ? Math.round(avgOrderValue) || 0 : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="ui-card ui-card-pad mb-6 py-4 flex items-center justify-between max-w-2xl shadow-sm">
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-xl px-4 py-2 flex-1 mr-4">
          <Search className="text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search customers by name, email, or location..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent font-medium text-sm"
          />
        </div>
        <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200/85 text-slate-700 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer hidden sm:block">Search</button>
      </div>

      {/* Customers Table */}
      <div className="ui-table-wrap">
        {isLoading ? (
          <div className="p-16 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4 text-emerald-500" size={36} />
            <p className="font-semibold text-xs uppercase tracking-wider">Loading Customer Insights...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th className="ui-th w-64">Customer Name</th>
                    <th className="ui-th w-64">Contact Info</th>
                    <th className="ui-th w-36 text-center">Total Orders</th>
                    <th className="ui-th w-36">Amount Spent</th>
                    <th className="ui-th w-40">Last Active</th>
                    <th className="ui-th w-32">Rank</th>
                    <th className="ui-th w-28">Points</th>
                    <th className="ui-th w-32 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedCustomers.map(customer => (
                    <tr key={customer.id} className="hover:bg-slate-50/40 transition-colors duration-150 group">
                      <td className="ui-td">
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 flex shrink-0 items-center justify-center font-bold shadow-sm">
                            {customer.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-bold text-slate-800 text-sm group-hover:text-emerald-600 transition-colors">{customer.name}</span>
                            <div className="flex items-center gap-1 text-[11px] text-slate-400 font-medium mt-1">
                              <MapPin size={11} className="text-slate-400 shrink-0" />
                              <span className="truncate max-w-[150px]" title={customer.location}>{customer.location}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="ui-td">
                        <div className="space-y-1 text-xs font-semibold">
                          <div className="flex items-center gap-2 text-slate-500">
                            <Mail size={11} className="text-slate-400 shrink-0" />
                            <span className="truncate max-w-[140px]" title={customer.email}>{customer.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-500">
                            <Phone size={11} className="text-slate-400 shrink-0" />
                            <span>{customer.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="ui-td text-center">
                        <span className="font-bold text-slate-600 text-xs bg-slate-100 border border-slate-200/40 px-2.5 py-1 rounded-full">{customer.totalOrders}</span>
                      </td>
                      <td className="ui-td font-extrabold text-slate-800">
                        ₹{customer.totalSpent.toLocaleString()}
                      </td>
                      <td className="ui-td font-semibold text-slate-500 text-xs">
                        {customer.lastOrder}
                      </td>
                      <td className="ui-td">
                        <span className={getStatusColorClass(customer.status)}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="ui-td font-extrabold text-yellow-600 text-xs">
                        <span className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-100 px-2.5 py-0.5 rounded-lg w-fit">
                          <Star size={11} className="fill-yellow-500 text-yellow-500" />
                          <span>{customer.loyaltyPoints}</span>
                        </span>
                      </td>
                      <td className="ui-td text-center">
                        <button
                          onClick={() => handleViewOrders(customer)}
                          className="bg-white border border-slate-200 hover:border-emerald-150 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 px-3.5 py-1.5 rounded-xl flex items-center gap-1.5 transition-all shadow-sm font-bold text-[10px] uppercase tracking-wider mx-auto cursor-pointer"
                        >
                          <Eye size={12} />
                          <span>History</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredCustomers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* Order History Modal */}
      {showOrderModal && selectedCustomer && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-[28px] border border-slate-100 shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col justify-between overflow-hidden animate-fade-in">
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-800 tracking-tight">
                  Purchase History Log
                </h2>
                <p className="text-xs text-slate-400 font-semibold mt-1">{selectedCustomer.name}</p>
              </div>
              <button
                onClick={() => setShowOrderModal(false)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-3 pr-4 ui-scrollbar">
              {selectedCustomer.orders.map((order, index) => (
                <div key={index} className="bg-slate-50 border border-slate-100/60 hover:bg-slate-100/40 rounded-2xl p-4 flex justify-between items-center cursor-pointer transition-all duration-200 group" onClick={() => handleViewBill(order, index)}>
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="font-extrabold text-slate-800 text-xs group-hover:text-emerald-600 transition-colors">{order.productName}</h3>
                      <span className="bg-blue-50 text-blue-700 border border-blue-100/60 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider">
                        Order #{index + 1}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Weight: {order.weight}</p>
                    <p className="text-[10px] text-slate-400 font-medium mt-0.5">Date: {order.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-extrabold text-slate-800 text-sm">₹{order.price}</p>
                    <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider mt-1 group-hover:underline">View Receipt</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-6 bg-slate-50 border-t border-slate-100 rounded-b-[28px] shrink-0">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Transactions:</span>
                <span className="font-extrabold text-slate-800 text-sm">{selectedCustomer.orders.length}</span>
              </div>
              <div className="flex justify-between items-center mt-2.5 pt-2.5 border-t border-slate-200/50">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Net Revenue:</span>
                <span className="font-extrabold text-emerald-600 text-lg">₹{selectedCustomer.totalSpent.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showBillModal && selectedOrder && (() => {
        const cust = selectedOrder.customer;
        const invNum = `INV-${new Date().getFullYear()}-${String(selectedOrder.orderNumber).padStart(4,'0')}`;
        const subtotal = parseFloat(selectedOrder.price) || 0;
        const gstRate = 0; // organic items typically 0% GST – change as needed
        const gstAmt = subtotal * gstRate;
        const grandTotal = subtotal + gstAmt;

        const printInvoice = () => {
          const w = window.open('', '_blank', 'width=800,height=900');
          w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Invoice ${invNum}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',sans-serif;background:#f1f5f9;padding:30px;color:#1e293b}
  .page{background:#fff;max-width:720px;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.12)}
  .header{background:linear-gradient(135deg,#10b981 0%,#059669 100%);padding:36px 40px;color:#fff}
  .header-top{display:flex;justify-content:space-between;align-items:flex-start}
  .brand{display:flex;align-items:center;gap:12px}
  .brand-icon{width:52px;height:52px;background:rgba(255,255,255,0.2);border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:26px}
  .brand-name{font-size:24px;font-weight:900;letter-spacing:-0.5px}
  .brand-sub{font-size:12px;opacity:0.8;margin-top:3px;letter-spacing:1px}
  .inv-badge{background:rgba(255,255,255,0.2);border-radius:10px;padding:10px 18px;text-align:right}
  .inv-badge .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1.5px}
  .inv-badge .num{font-size:20px;font-weight:800;margin-top:2px}
  .header-meta{display:flex;gap:32px;margin-top:24px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.2)}
  .meta-item .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1px}
  .meta-item .val{font-size:14px;font-weight:700;margin-top:3px}
  .body{padding:36px 40px}
  .two-col{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:32px}
  .info-box{background:#f8fafc;border-radius:12px;padding:18px}
  .info-box h4{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;margin-bottom:10px}
  .info-box p{font-size:13px;color:#374151;line-height:1.7}
  .info-box .main{font-size:15px;font-weight:700;color:#111827}
  table{width:100%;border-collapse:collapse;margin-bottom:28px}
  thead tr{background:linear-gradient(135deg,#ecfdf5,#d1fae5)}
  th{padding:12px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#047857}
  th:last-child,td:last-child{text-align:right}
  td{padding:13px 14px;font-size:13px;color:#374151;border-bottom:1px solid #f1f5f9}
  tr:last-child td{border-bottom:none}
  tbody tr:hover{background:#fafffe}
  .item-name{font-weight:600;color:#111827}
  .item-sub{font-size:11px;color:#94a3b8;margin-top:3px}
  .totals{margin-left:auto;width:260px}
  .total-row{display:flex;justify-content:space-between;padding:7px 0;font-size:13px;color:#64748b}
  .total-row.grand{border-top:2px solid #10b981;margin-top:6px;padding-top:12px;font-size:17px;font-weight:800;color:#111827}
  .total-row.grand .amount{color:#10b981}
  .footer{background:#f8fafc;border-top:1px solid #e2e8f0;padding:24px 40px;display:flex;justify-content:space-between;align-items:center}
  .footer-left{font-size:12px;color:#64748b;line-height:1.8}
  .thanks{font-size:13px;font-weight:700;color:#10b981;text-align:right}
  .thanks span{display:block;font-size:11px;color:#94a3b8;font-weight:400;margin-top:3px}
  .watermark{text-align:center;padding:16px;font-size:11px;color:#cbd5e1}
  @media print{body{background:#fff;padding:0}.page{box-shadow:none;border-radius:0}}
</style></head><body>
<div class="page">
  <div class="header">
    <div class="header-top">
      <div class="brand">
        <div class="brand-icon">🌿</div>
        <div>
          <div class="brand-name">Manthuli Organic Shop</div>
          <div class="brand-sub">Fresh • Organic • Natural</div>
        </div>
      </div>
      <div class="inv-badge">
        <div class="label">Invoice</div>
        <div class="num">${invNum}</div>
      </div>
    </div>
    <div class="header-meta">
      <div class="meta-item">
        <div class="label">Invoice Date</div>
        <div class="val">${selectedOrder.date || new Date().toLocaleDateString('en-GB').replace(/\//g,'.')}</div>
      </div>
      <div class="meta-item">
        <div class="label">Order Ref</div>
        <div class="val">#${String(selectedOrder.orderNumber).padStart(4,'0')}</div>
      </div>
      <div class="meta-item">
        <div class="label">Status</div>
        <div class="val">Completed</div>
      </div>
    </div>
  </div>

  <div class="body">
    <div class="two-col">
      <div class="info-box">
        <h4>Bill To</h4>
        <p class="main">${cust?.name || '—'}</p>
        <p>${cust?.phone || 'N/A'}</p>
        <p>${cust?.email || ''}</p>
        <p style="margin-top:6px;color:#64748b;font-size:12px">${cust?.location || ''}</p>
      </div>
      <div class="info-box">
        <h4>From</h4>
        <p class="main">Manthuli Organic Shop</p>
        <p>📞 +91-9876543210</p>
        <p>📧 info@manthuliorganic.com</p>
        <p style="margin-top:6px;color:#64748b;font-size:12px">123 Organic Lane, Green City</p>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Item Description</th>
          <th>Weight / Unit</th>
          <th>Qty</th>
          <th>Unit Price</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="color:#94a3b8;font-weight:600">01</td>
          <td>
            <div class="item-name">${selectedOrder.productName || 'Product'}</div>
            <div class="item-sub">Organic · Premium Quality</div>
          </td>
          <td>${selectedOrder.weight || '—'}</td>
          <td style="text-align:center">1</td>
          <td>₹${subtotal.toLocaleString('en-IN')}</td>
          <td style="font-weight:700;color:#111827">₹${subtotal.toLocaleString('en-IN')}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>₹${subtotal.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>GST (${(gstRate*100).toFixed(0)}%)</span><span>₹${gstAmt.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>Delivery</span><span style="color:#10b981;font-weight:700">FREE</span></div>
      <div class="total-row grand"><span>Total</span><span class="amount">₹${grandTotal.toLocaleString('en-IN')}</span></div>
    </div>
  </div>

  <div class="footer">
    <div class="footer-left">
      <strong>Payment Info</strong><br>
      Mode: Cash on Delivery<br>
      GSTIN: 29AAACH7409R1ZZ
    </div>
    <div class="thanks">
      Thank you for your order! 🌿
      <span>We appreciate your trust in organic products</span>
    </div>
  </div>
  <div class="watermark">This is a computer-generated invoice and does not require a physical signature.</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`);
          w.document.close();
        };

        return (
          <div className="ui-modal-overlay">
            {/* Invoice Preview Card */}
            <div className="bg-white rounded-[28px] border border-slate-100/80 shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto animate-fade-in flex flex-col justify-between">

              {/* Green Header */}
              <div className="bg-gradient-to-br from-emerald-600 to-green-700 p-7 text-white rounded-t-[28px]">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl shadow-sm">🌿</div>
                    <div>
                      <div className="font-extrabold text-lg tracking-tight">Manthuli Organic</div>
                      <div className="text-[10px] opacity-80 uppercase tracking-wider mt-1">Fresh • Organic • Natural</div>
                    </div>
                  </div>
                  <button onClick={() => setShowBillModal(false)} className="bg-white/20 border-0 text-white w-8 h-8 rounded-lg cursor-pointer flex items-center justify-center hover:bg-white/35 transition-all text-sm font-bold">✕</button>
                </div>
                <div className="flex gap-6 mt-6 pt-4 border-t border-white/25">
                  {[
                    {label:'INVOICE',   val: invNum},
                    {label:'DATE',      val: selectedOrder.date || '—'},
                    {label:'REF #', val: `#${String(selectedOrder.orderNumber).padStart(4,'0')}`},
                  ].map(m => (
                    <div key={m.label}>
                      <div className="text-[9px] opacity-75 uppercase tracking-wider font-extrabold">{m.label}</div>
                      <div className="font-extrabold text-sm mt-1">{m.val}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-7 space-y-6">
                {/* Bill To / From */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    {title:'Bill To', lines:[cust?.name, cust?.phone, cust?.email, cust?.location]},
                    {title:'From',    lines:['Manthuli Organic Shop','📞 +91-9876543210','info@manthuliorganic.com','123 Organic Lane, Green City']},
                  ].map(b => (
                    <div key={b.title} className="bg-slate-50 border border-slate-200/50 rounded-2xl p-4">
                      <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">{b.title}</div>
                      {b.lines.filter(Boolean).map((l,i) => (
                        <div key={i} className={`text-xs ${i===0?'text-slate-800 font-bold':'text-slate-500 font-medium'} leading-normal truncate`}>{l}</div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Items Table */}
                <div className="rounded-2xl overflow-hidden border border-slate-200/60 shadow-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-emerald-50/60">
                        {['Item','Weight','Qty','Price','Total'].map(h => (
                          <th key={h} className="p-3 text-left font-bold uppercase tracking-wider text-emerald-800">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      <tr>
                        <td className="p-3 font-bold text-slate-800">
                          {selectedOrder.productName}
                          <div className="text-[10px] text-slate-400 font-medium mt-0.5">Organic · Premium</div>
                        </td>
                        <td className="p-3 text-slate-500 font-semibold">{selectedOrder.weight || '—'}</td>
                        <td className="p-3 text-slate-500 font-semibold text-center">1</td>
                        <td className="p-3 text-slate-600 font-bold">₹{subtotal.toLocaleString('en-IN')}</td>
                        <td className="p-3 font-extrabold text-slate-800 text-right">₹{subtotal.toLocaleString('en-IN')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="ml-auto w-56 space-y-1.5 border-t border-slate-100 pt-3">
                  {[
                    {label:'Subtotal', val:`₹${subtotal.toLocaleString('en-IN')}`},
                    {label:`GST (${(gstRate*100).toFixed(0)}%)`, val: gstAmt===0?'₹0':'₹'+gstAmt.toLocaleString('en-IN')},
                    {label:'Delivery Fee', val:'FREE', green:true},
                  ].map(row => (
                    <div key={row.label} className="flex justify-between text-xs text-slate-400 font-bold uppercase tracking-wider">
                      <span>{row.label}</span><span className={row.green?'text-emerald-600 font-extrabold':''}>{row.val}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-emerald-500/30 pt-3 mt-2 font-extrabold text-sm">
                    <span className="text-slate-800">Grand Total</span>
                    <span className="text-emerald-600 text-base">₹{grandTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Note */}
                <div className="bg-emerald-50/60 border border-emerald-100/60 rounded-xl p-3 text-[10px] text-emerald-700 font-extrabold text-center uppercase tracking-wider">
                  🌿 Thank you for supporting local organic agriculture!
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button onClick={printInvoice}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 text-white border-0 py-3 rounded-xl font-bold text-xs uppercase tracking-wider cursor-pointer shadow-md shadow-emerald-500/10 flex items-center justify-center gap-1.5 hover:-translate-y-0.5 transition-all">
                    <span>🖨️ Print / Save</span>
                  </button>
                  <button onClick={() => setShowBillModal(false)}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border-0 py-3 rounded-xl font-bold text-xs uppercase tracking-wider cursor-pointer transition-colors shadow-sm">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {filteredCustomers.length === 0 && (
        <div className="ui-card p-12 text-center mt-6 shadow-sm">
          <div className="w-16 h-16 bg-slate-50 border border-slate-200/60 rounded-2xl flex items-center justify-center mx-auto mb-4 shrink-0 shadow-inner">
            <Users size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 font-semibold text-sm">No customers matching your search filters were found.</p>
        </div>
      )}
    </div>
  );
};

export default Customers;